import { Container, Grid, Skeleton, styled } from '@mui/material';
import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, ToastSeverity, theme, useToast } from 'thermia-react-container';
import { RegistrationsContext } from '../../context/contextProvider';
import {Registration, RegistrationStatusCode} from '../../types/registrationTypes';
import {ApiErrorHelper, RegistrationConflictError} from '../../validation/Errors';
import RegistrationTabs from './components/registrationTab';
import RegistrationCustomer from './views/customer/registrationCustomer';
import RegistrationDevices from './views/devices/registrationDevices';
import RegistrationLocation from './views/location/registrationLocation';
import RegistrationSystem from './views/system/registrationSystem';
import {useCurrentPartner} from "./hooks/useCurrentPartner";

export const NEW_REGISTRATION_ID = 'new';

export const StyledContainer = styled('div')(({ theme }) => ({
  minHeight: '300px',
  paddingLeft: '42px',
  paddingRight: '42px',
  paddingTop: '30px',
  paddingBottom: '30px',
  background: theme.palette.backgroundExtraLight.main
}));

const RegistrationTopWrapper: FC = () => {
  const { devices, config, registrations } = useContext(RegistrationsContext);
  const { activeRegistration, removeActiveRegistration, setActiveRegistration, fetchRegistration, createRegistration, updateCustomer, updateLocation, updateSystemDescription, updateDevices} =
    registrations;
  const { fetchDevices } = devices;
  const { installationConfig } = config;

  const [loadingReg, setLoadingReg] = useState<boolean>(false);
  const [loadingDevices, setLoadingDevices] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  let { id = NEW_REGISTRATION_ID, tabPage } = useParams<{ id: string; tabPage: string }>();
  const { t } = useTranslation('registrations');
  const { addToast } = useToast();
  const [hasUnsavedData, setHasUnsavedData] = useState<boolean>(false);
  const navigate = useNavigate();
  const partner = useCurrentPartner();
  const defaultCountry = partner.market;
  const defaultValues = installationConfig?.registrationDefaultValues.find((c) => c.salesMarket === partner.market);
  const { state } = useLocation();
  const sentDataState: { deviceIds?: string[] } | null = state;
  const initialized = !!activeRegistration?.partnerId;
  const shouldResetRegistrationOnEntry = useRef<Boolean>(true);

  if (!tabPage) {
    tabPage = 'customer';
  }

  const setDefaultValuesForNewRegistration = useCallback(async () => {
    // Get devices to include in registration
    let existingDevices = devices.devices !== null ? devices.devices : [];
    if(existingDevices.length <= 0) {
      const fetchedDevices = await fetchDevices();
      if(fetchedDevices) {
        existingDevices = fetchedDevices;
      }
    }
    const registrationDevices = existingDevices.filter(d => sentDataState?.deviceIds?.some(sd => sd === d.id)) ?? [];
    const newRegistration: Registration = {
      id: NEW_REGISTRATION_ID,
      partnerId: partner.id,
      customer: {
        phone: defaultValues?.phoneCountryCode,
        country: partner.market,
        type: defaultValues?.customerCode,
        insuranceType: defaultValues?.insuranceType
      },
      devices: { items: registrationDevices }
    };
    setActiveRegistration(newRegistration);
  }, [
    defaultValues?.customerCode,
    defaultValues?.insuranceType,
    defaultValues?.phoneCountryCode,
    partner.id,
    partner.market,
    sentDataState?.deviceIds,
    setActiveRegistration
  ]);

  const handleFetchRegistrations = useCallback(async () => {
    setLoadingReg(true);
    if (id === NEW_REGISTRATION_ID) {
      await setDefaultValuesForNewRegistration();
      setLoadingReg(false);
      setError(false);
      return;
    }
    try {
      const current = await fetchRegistration(id!.toString());
      if (current) {
        setActiveRegistration(current);
        setHasUnsavedData(false);
      }
      setError(false);
    } catch (error) {
      addToast(t('registrations.general.errorFetchRegistration'), ToastSeverity.ERROR);
      setError(true);
    }
    setLoadingReg(false);
  }, [addToast, fetchRegistration, id, setActiveRegistration, setDefaultValuesForNewRegistration, t]);

  useEffect(() => {
    const hasActiveRegistration = activeRegistration && id !== NEW_REGISTRATION_ID;
    if (!hasActiveRegistration && shouldResetRegistrationOnEntry.current) {
      shouldResetRegistrationOnEntry.current = false;
      removeActiveRegistration();
    }
    if (!activeRegistration && !loadingReg && !error) {
      handleFetchRegistrations();
    }
  }, [activeRegistration, handleFetchRegistrations, loadingReg, error, id, setDefaultValuesForNewRegistration, removeActiveRegistration]);

  useEffect(() => {
    const getDevices = async () => {
      setLoadingDevices(true);
      try {
        await fetchDevices();
      } catch (error) {
        addToast(t('registrations.general.errorFetchDevices'), ToastSeverity.ERROR);
      }
      setLoadingDevices(false);
    };
    if (!devices && !loadingDevices) {
      getDevices();
    }
  }, [devices, t, addToast, loadingDevices, fetchDevices]);

  const preventSwitchTabDueToUnsavedData = (value: boolean) => {
    setHasUnsavedData(value);
  };

  async function onUpdate(
    registration: Registration,
    action: () => Promise<Registration>
  ): Promise<Registration> {
    try {
      let updatedRegistration: Registration;
      if (registration.id === NEW_REGISTRATION_ID) {
        updatedRegistration = await createRegistration(registration);
        registration.id = updatedRegistration.id;
        registration.revision = updatedRegistration.revision;
        preventSwitchTabDueToUnsavedData(false);
      }
      updatedRegistration = await action();
      preventSwitchTabDueToUnsavedData(false);

      addToast(t('registrations.general.saved'), ToastSeverity.SUCCESS);
      navigate(`/registrations/${updatedRegistration.id}/${tabPage}`);
      setHasUnsavedData(false);
      return updatedRegistration;
    } catch (error) {
      if (error instanceof RegistrationConflictError && error.isConflict) {
        if(error.isRegistrationCompleted) {
          addToast(t('registrations.general.errorRegistrationIsAlreadyCompleted'), ToastSeverity.ERROR);
        }
        else if(await error.isRegistrationConflict()) {
          addToast(t('registrations.general.errorRegistrationConflict', {dateStr: new Date(registration.updated ?? "").toLocaleString()}), ToastSeverity.WARNING);
        }
        else if(await error.isDeviceConflict()) {
          addToast(t('registrations.general.errorRegistrationConflictDevice'), ToastSeverity.WARNING);
          await fetchDevices();
        }
        throw error;
      }

      if (ApiErrorHelper.isOnsiteVersionInvalid(error)) {
        addToast(t('registrations.general.errorOnsiteVersionIsInvalid'), ToastSeverity.ERROR);
        throw error;
      }

      if (ApiErrorHelper.isNotFound(error)) {
        addToast(t('registrations.general.errorRegistrationUnableToUpdateDueItHasBeenDeleted'), ToastSeverity.WARNING);
        navigate(`/registrations`);
        throw error;
      }

      addToast(t('registrations.general.errorRegistrationUpdate'), ToastSeverity.ERROR);
      throw error;
    }
  }

  const isReady = initialized && defaultCountry;
  return (
    <Container key={activeRegistration?.id} maxWidth="xl" sx={{ marginTop: theme.spacing(2) }}>
      <RegistrationTabs
        registrationIsCompleted={activeRegistration?.status === RegistrationStatusCode.Completed}
        registration={activeRegistration}
        hasUnsavedData={hasUnsavedData}
        preventSwitchTabDueToUnsavedData={preventSwitchTabDueToUnsavedData}
        disabled={activeRegistration?.status === RegistrationStatusCode.Completed}
      />
      {!isReady && !error && <Skeleton width={'100%'} height={'64px'} />}
      {error && (
        <Grid container justifyContent={'center'}>
          <Button
            variant="contained"
            style={{ backgroundColor: theme.palette.error.main }}
            title={`${t('registrations.general.errorFetchRegistration')} - ${t('registrations.general.retry')}`}
            loading={loadingReg}
            onClick={handleFetchRegistrations}
          />
        </Grid>
      )}
      {isReady && tabPage === 'customer' && (
        <RegistrationCustomer
          registration={activeRegistration}
          defaultCountry={defaultCountry}
          updateRegistration={(registration) => onUpdate(registration, () =>  updateCustomer(registration))}
          preventSwitchTabDueToUnsavedData={preventSwitchTabDueToUnsavedData}
          disabled={activeRegistration?.status === RegistrationStatusCode.Completed}
        />
      )}
      {isReady && tabPage === 'location' && (
        <RegistrationLocation
          registration={activeRegistration}
          updateRegistration={(registration) => onUpdate(registration, () =>  updateLocation(registration))}
          defaultCountry={defaultCountry}
          preventSwitchTabDueToUnsavedData={preventSwitchTabDueToUnsavedData}
          disabled={activeRegistration?.status === RegistrationStatusCode.Completed}
        />
      )}
      {isReady && tabPage === 'devices' && (
        <RegistrationDevices
          registration={activeRegistration}
          updateRegistration={(registration) => onUpdate(registration, async () =>  {
            const updatedReg = await updateDevices(registration);
            fetchDevices();
            return updatedReg;
          })}
          preventSwitchTabDueToUnsavedData={preventSwitchTabDueToUnsavedData}
          disabled={activeRegistration?.status === RegistrationStatusCode.Completed}
          registrationIsCompleted={activeRegistration?.status === RegistrationStatusCode.Completed}
        />
      )}
      {isReady && tabPage === 'system' && (
        <RegistrationSystem
          registration={activeRegistration}
          updateRegistration={(registration) => onUpdate(registration, () =>  updateSystemDescription(registration))}
          preventSwitchTabDueToUnsavedData={preventSwitchTabDueToUnsavedData}
          disabled={activeRegistration?.status === RegistrationStatusCode.Completed}
        />
      )}
    </Container>
  );
};

export default RegistrationTopWrapper;
